/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import React from 'react';
import { commitMutation, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { Button, Divider, Grid, Loader, Modal } from 'semantic-ui-react';
import {
  Analytics,
  GlobalNotifications,
  Intent,
  MobileModal
} from '../../Modules';
import {
  FloatingLabelInput as Input,
  FloatingLabelDateTime as DateTime,
  FloatingLabelDateTimePicker as DateTimePicker,
  Success,
  SelectableBlock
} from '../../Elements';
import styleVariables from '../../variables';
import documentStyles from '../Document.scss';
import styles from './Invoice.scss';
import Formsy from 'formsy-react';
import NumberFormat from 'react-number-format';
import currencyFormatter from 'currency-formatter';
import paymentsHelper from '../../../lib/utils/payments';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import Script from 'react-load-script';
import NoPaymentMethods from '../../App/PaymentMethods/NoPaymentMethods';
import moment from 'moment';
import { produce } from 'immer';


export default class PayInvoiceFlow extends React.Component {
  static propTypes = {
    node: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    trigger: PropTypes.node.isRequired,
    viewer: PropTypes.object,
    window: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = Object.assign(
      {
        squareError: false,
        squareScriptLoading: true,
        squareFormLoading: true
      },
      this.initializePayModal()
    );
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onSquareValidSubmit = this.onSquareValidSubmit.bind(this);
    this.onSquareInvalidSubmit = this.onSquareInvalidSubmit.bind(this);
    this.onSquareScriptLoaded = this.onSquareScriptLoaded.bind(this);
    this.onSquareError = this.onSquareError.bind(this);
    this.onManualPaymentValidSubmit = this.onManualPaymentValidSubmit.bind(
      this
    );
    this.onManualPaymentInvalidSubmit = this.onManualPaymentInvalidSubmit.bind(
      this
    );
    this.getInvoiceTotal = this.getInvoiceTotal.bind(this);
    this.getTotalPaid = this.getTotalPaid.bind(this);
    this.getMinimumBalance = this.getMinimumBalance.bind(this);
    this.getPaymentAmount = this.getPaymentAmount.bind(this);
    this.initializeCard = this.initializeCard.bind(this);
    this.tokenizeSquare = this.tokenizeSquare.bind(this);
    this.fieldChange = this.fieldChange.bind(this);
    this.textFieldChange = this.textFieldChange.bind(this);
    this.handlePaymentMethodChange = this.handlePaymentMethodChange.bind(this);
    this.onPaymentAmountChange = this.onPaymentAmountChange.bind(this);
    this.customAmountFieldChange = this.customAmountFieldChange.bind(this);
    this.chargeSquare = this.chargeSquare.bind(this);
    this.paymentMethodsExist = this.paymentMethodsExist.bind(this);
  }

  onOpen() {
    this.props.onOpen();
  }

  onClose() {
    this.props.onClose();
    this.setState(Object.assign(this.state, this.initializePayModal()));
  }

  onSquareValidSubmit() {
    this.tokenizeSquare(this.state.card).then(token => {
      this.setState(
        produce(draft => {
          /* eslint-disable no-param-reassign */
          draft.payment.nonce = token;
        }), () => this.chargeSquare(() => this.setState({ step: 'success' }))
      );
    }).catch(() => {
      this.setState({ submitting: false });
    });
    this.setState({ submitted: true, submitting: true });
  }

  onSquareInvalidSubmit() {
    GlobalNotifications.show({
      message: 'Unable to pay invoice',
      intent: Intent.DANGER
    });
    this.setState({ submitted: true });
  }

  onSquareScriptLoaded() {
    this.setState({ squareScriptLoading: false });
    const squarePaymentMethod = this.props.node.accepted_payment_methods.edges.filter(
      method => method.node.__typename === 'SquarePaymentMethod'
    )[0].node;
    if (!window.Square) {
      throw new Error('Square.js failed to load properly');
    }
    const payments = window.Square.payments(squarePaymentMethod.application_id, squarePaymentMethod.location_id);
    try {
      this.initializeCard(payments).then(card => {
        this.setState({ card });
      });
    } catch (e) {
      return;
    }
  }

  onSquareError() {
    this.setState({ squareError: true });
  }

  onManualPaymentValidSubmit() {
    this.createManualPayment(() => this.setState({ step: 'success' }));
    this.setState({ submitted: true, submitting: true });
  }

  onManualPaymentInvalidSubmit() {
    GlobalNotifications.show({
      message: 'Unable to pay invoice',
      intent: Intent.DANGER
    });
    this.setState({ submitted: true });
  }

  onPaymentAmountChange(e, { name }) {
    if (name !== this.state.paymentAmount) {
      this.setState({ paymentAmount: name });
    }
  }

  getInvoiceTotal() {
    return this.props.node.total_cents || 0;
  }

  getTotalPaid() {
    return this.props.node.total_paid_cents || 0;
  }

  getMinimumBalance() {
    let paymentsDue = 0;
    let lastItemDue = 0;
    // All i_pplan_items with due_at in the past are now due
    this.props.node.i_pplan_items.edges.slice().map((pplanItem, index) => {
      if (new Date(pplanItem.node.due_at) < new Date()) {
        paymentsDue += pplanItem.node.amount_cents || 0;
        lastItemDue = index;
      }
      return pplanItem;
    });
    // If down payment exists, it is always due regardless of down_payment_due_at
    if (this.props.node.down_payment_cents) {
      paymentsDue += this.props.node.down_payment_cents || 0;
    } else if (this.props.node.i_pplan_items.edges.length > lastItemDue + 1) {
      // If down payment does not exist, the next payment that is due
      // is the first one that wasn't already due in the past
      // (assuming another i_pplan_item exists)
      paymentsDue += this.props.node.i_pplan_items.edges[lastItemDue + 1].node
        .amount_cents;
    }
    // Total payments made towards this invoice
    const paymentsMade = this.getTotalPaid();
    // Minimum balance due today
    const minimumBalance = paymentsDue - paymentsMade;
    // Return remaining balance. If more payments have been made than are due,
    // return zero as the minimum balance
    return minimumBalance < 0 ? 0 : minimumBalance;
  }

  getPaymentAmount() {
    return (() => {
      switch (this.state.paymentAmount) {
        case 'minimum':
          return this.getMinimumBalance();
        case 'total':
          return this.getInvoiceTotal() - this.getTotalPaid();
        default:
          return this.state.payment.amount_cents;
      }
    })();
  }

  initializeCard(payments) {
    const promise = new Promise(resolve => {
      payments.card().then(card => {
        card.attach('#card-container').then(() => resolve(card));
      });
    });
    return promise;
  }

  tokenizeSquare(paymentMethod) {
    return new Promise((resolve, reject) => {
      paymentMethod.tokenize().then(tokenResult => {
        if (tokenResult.status === 'OK') {
          resolve(tokenResult.token);
        } else {
          let errorMessage = `Tokenization failed-status: ${tokenResult.status}`;
          if (tokenResult.errors) {
            errorMessage += ` and errors: ${JSON.stringify(
              tokenResult.errors
            )}`;
          }
          reject(errorMessage);
        }
      });
    });
  }

  initializePayModal() {
    return {
      errors: {},
      paymentAmount: null,
      payment: {
        // TODO: replace with payment method for next payment
        payment_method: {},
        amount_cents: null,
        amount_currency: 'USD',
        notes: null
      },
      step: 'selectAmount',
      submitting: false,
      submitted: false
    };
  }

  createManualPayment(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add payment',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully added payment',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-manual-payment');
    };

    const mutation = graphql`
      mutation Pay_createManualPaymentMutation(
        $input: CreateManualPaymentInput!
      ) {
        createManualPayment(input: $input) {
          invoice {
            ...InvoiceContainer_node
          }
        }
      }
    `;

    const payment = _.pick(nullifyEmptyStrings(this.state.payment), [
      'name',
      'notes',
      'amount_cents',
      'amount_currency',
      'paid_at'
    ]);

    payment.paid_at = moment(payment.paid_at).toDate().toISOString();

    payment.amount_cents = this.getPaymentAmount();

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          invoice_id: this.props.node.id,
          payment_method_id: this.state.payment.payment_method.id,
          ...payment
        }
      },
      onCompleted,
      onError
    });
  }

  chargeSquare(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      this.setState({ submitting: false });
      GlobalNotifications.show({
        message: 'Unable to process payment',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      this.setState({ submitting: false });
    };

    const mutation = graphql`
      mutation Pay_chargeSquareMutation($input: ChargeSquareInput!) {
        chargeSquare(input: $input) {
          invoice {
            ...InvoiceContainer_node
          }
        }
      }
    `;

    const squarePayment = _.pick(nullifyEmptyStrings(this.state.payment), [
      'name',
      'nonce',
      'amount_currency'
    ]);

    const squarePaymentMethod = this.props.node.accepted_payment_methods.edges.filter(
      method => method.node.__typename === 'SquarePaymentMethod'
    )[0];

    squarePayment.amount_cents = this.getPaymentAmount();

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          invoice_id: this.props.node.id,
          square_payment_method_id: squarePaymentMethod.node.id,
          ...squarePayment
        }
      },
      onCompleted,
      onError
    });
  }

  customAmountFieldChange(e) {
    this.fieldChange('amount_cents', e.target.value * 100);
  }

  fieldChange(key, value) {
    this.setState(
      produce(draft => {
        // eslint-disable-next-line
        draft.payment[key] = value;
      })
    );
  }

  textFieldChange(e) {
    this.fieldChange(e.target.name, e.target.value);
  }

  handlePaymentMethodChange(e, method) {
    this.fieldChange('payment_method', method);
  }

  paymentMethodsExist() {
    return this.props.node.accepted_payment_methods.edges.length > 0;
  }

  render() {
    const { window } = this.props;
    const squarePaymentMethod = this.props.node.accepted_payment_methods.edges.filter(
      method => method.node.__typename === 'SquarePaymentMethod'
    )[0];

    const DateInput =
      window.width < styleVariables.tabletBreakpoint
        ? DateTime
        : DateTimePicker;

    const paymentOptions = (
      <div>
        {this.paymentMethodsExist() &&
          <div>
            <h3>Select a Payment Method</h3>
            <br />
            {this.props.node.accepted_payment_methods.edges.map((method, idx) =>
              <SelectableBlock
                onChange={e => this.handlePaymentMethodChange(e, method.node)}
                key={idx}
                radio
                checked={
                  this.state.payment.payment_method.id === method.node.id
                }
                style={{ marginBottom: '2em' }}
              >
                <h4 style={{ marginBottom: 0 }}>
                  {paymentsHelper.methods(method.node)}
                </h4>
              </SelectableBlock>
            )}
          </div>}
        {!this.props.node.can_edit &&
          !this.paymentMethodsExist() &&
          <div>
            <h3>No payment methods enabled</h3>
            <p>
              Please contact us for additional payment options and instructions
            </p>
          </div>}
        {this.props.node.can_edit &&
          !this.paymentMethodsExist() &&
          <NoPaymentMethods
            viewer={this.props.viewer}
            relay={this.props.relay}
            node={this.props.node}
            onClose={() => (
              this.paymentMethodsExist() ? () => {} : this.onClose()
            )}
            onOpen={this.onOpen}
            open={this.props.open}
          />}
      </div>
    );

    const ManualPaymentForm = (
      <Formsy.Form
        ref='manualPaymentForm'
        onValidSubmit={this.onManualPaymentValidSubmit}
        onInvalidSubmit={this.onManualPaymentInvalidSubmit}
        style={{ marginTop: '2em' }}
      >
        <Divider horizontal className={documentStyles.heading}>
          Admin Only
        </Divider>
        <div
          style={
            window.width >= styleVariables.tabletBreakpoint ? {
              display: 'flex'
            } : {}
          }
        >
          <div
            style={
              window.width >= styleVariables.tabletBreakpoint ? {
                flex: 1,
                marginRight: '1em'
              } : {}
            }
          >
            <Input
              name='name'
              label='Paid By'
              placeholder='Jane Smith'
              value={this.state.payment.name || ''}
              onChange={this.textFieldChange}
              submitted={this.state.submitted}
              required
              validationError='Name is required'
              fluid
            />
          </div>
          <div>
            <DateInput
              dateOnly
              value={this.state.payment.paid_at || ''}
              onChange={this.textFieldChange}
              name='paid_at'
              fluid
              autocomplete='off'
              dateLabel='Paid On'
              style={{
                marginTop: '1.25em'
              }}
              required
              validationError='Date is required'
              inline
              viewer={null}
              submitted={this.state.submitted}
            />
          </div>
        </div>
        {this.state.paymentAmount === 'other' &&
          <Input
            name='amount_cents'
            label='Amount'
            type='currency'
            placeholder='$500.00'
            value={
              this.state.payment.amount_cents
                ? this.state.payment.amount_cents / 100
                : ''
            }
            onChange={this.customAmountFieldChange}
            submitted={this.state.submitted}
            required
            validationError='Amount is required'
          >
            <NumberFormat
              thousandSeparator
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
            />
          </Input>}
        <Input
          name='notes'
          label='Notes'
          value={this.state.payment.notes || ''}
          onChange={this.textFieldChange}
          submitted={this.state.submitted}
          autoHeight
          fluid
        />
      </Formsy.Form>
    );

    const PayFields = (
      <div>
        {this.state.payment.payment_method.__typename ===
          'CheckPaymentMethod' &&
          <div>
            <h3>Instructions</h3>
            <p>
              Please write a check to{' '}
              <strong>{this.state.payment.payment_method.pay_to}</strong> and
              mail to:
            </p>
            <strong style={{ whiteSpace: 'pre-line' }}>
              {this.state.payment.payment_method.address}
            </strong>
            {this.props.viewer && ManualPaymentForm}
          </div>}
        {this.state.payment.payment_method.__typename === 'CashPaymentMethod' &&
          <div>
            <h3>Instructions</h3>
            <div
              dangerouslySetInnerHTML={{
                __html: this.state.payment.payment_method.instructions
              }}
            />
            {this.props.viewer && ManualPaymentForm}
          </div>}
        {this.state.payment.payment_method.__typename ===
          'CustomPaymentMethod' &&
          <div>
            <h3>Instructions</h3>
            <p style={{ whiteSpace: 'pre-line' }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: this.state.payment.payment_method.instructions
                }}
              />
            </p>
            {this.props.viewer && ManualPaymentForm}
          </div>}
        {this.state.payment.payment_method.__typename ===
          'SquarePaymentMethod' &&
          <div>
            <Formsy.Form
              ref='squareForm'
              onValidSubmit={this.onSquareValidSubmit}
              onInvalidSubmit={this.onSquareInvalidSubmit}
            >
              <h3>Enter credit card information</h3>
              <br />
              <label htmlFor='name'>Name</label>
              <Input
                name='name'
                id='name'
                placeholder='Jane Smith'
                required
                validationError='Name is required'
                value={this.state.payment.name || ''}
                onChange={this.textFieldChange}
                submitted={this.state.submitted}
              />
            </Formsy.Form>
            <form id='payment-form'>
              <div id='card-container'></div>
            </form>
            <div id='payment-status-container'></div>
          </div>}
      </div>
    );

    const SquareScript = (
      <Script
        url='https://web.squarecdn.com/v1/square.js'
        onCreate={() => {}}
        onError={() => this.onSquareError}
        onLoad={this.onSquareScriptLoaded}
      />
    );

    const BalanceOption = ({ amount, description, label }) =>
      <Grid>
        <Grid.Row>
          <Grid.Column width={10}>
            <strong>
              {label}
            </strong>
          </Grid.Column>
          <Grid.Column width={6} textAlign='right'>
            {amount}
          </Grid.Column>
        </Grid.Row>
        {description &&
          <Grid.Row style={{ paddingTop: '0em' }}>
            <Grid.Column width={12}>
              <p className={styles.paymentAmountDescription}>
                {description}
              </p>
            </Grid.Column>
            <Grid.Column width={4} />
          </Grid.Row>}
      </Grid>;

    const SelectAmount = (
      <div>
        <h3>Select a Payment Amount</h3>
        <br />
        {this.getMinimumBalance() !== 0 &&
          <SelectableBlock
            onChange={this.onPaymentAmountChange}
            name='minimum'
            value='minimum'
            radio
            checked={this.state.paymentAmount === 'minimum'}
            style={{ marginBottom: '2em' }}
          >
            <BalanceOption
              label='Minimum Payment Due'
              amount={currencyFormatter.format(this.getMinimumBalance() / 100, {
                code: 'USD'
              })}
              description='Pay the minimum balance due based on your payment plan and any past payments.'
            />
          </SelectableBlock>}
        <SelectableBlock
          onChange={this.onPaymentAmountChange}
          name='total'
          value='total'
          radio
          checked={this.state.paymentAmount === 'total'}
          style={{ marginBottom: '2em' }}
        >
          <BalanceOption
            label='Total Balance'
            amount={currencyFormatter.format(
              (this.getInvoiceTotal() - this.getTotalPaid()) / 100,
              {
                code: 'USD'
              }
            )}
            description='Pay the full balance on this invoice adjusted for any existing payments, refunds, and credits.'
          />
        </SelectableBlock>
        <SelectableBlock
          onChange={this.onPaymentAmountChange}
          name='other'
          value='other'
          fitted={false}
          radio
          checked={this.state.paymentAmount === 'other'}
        >
          <BalanceOption label='Other' />
        </SelectableBlock>
        {this.state.paymentAmount === 'other' &&
          <Formsy.Form
            style={{
              marginTop: '2em'
            }}
            key='otherAmountForm'
          >
            <label htmlFor='amount_cents'>Amount</label>
            <Input
              name='amount_cents'
              id='amount_cents'
              type='currency'
              placeholder='$500.00'
              value={
                this.state.payment.amount_cents
                  ? this.state.payment.amount_cents / 100
                  : ''
              }
              onChange={this.customAmountFieldChange}
              submitted={this.state.submitted}
              required
              validationError='Amount is required'
            >
              <NumberFormat
                thousandSeparator
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale
              />
            </Input>
          </Formsy.Form>}
      </div>
    );

    const payButtonText = (() => {
      const formattedAmount = currencyFormatter.format(
        this.getPaymentAmount() / 100,
        {
          code: this.state.payment.amount_currency
        }
      );

      if (this.state.submitting) {
        return 'Processing Payment';
      }
      if (
        this.state.payment.payment_method.__typename === 'SquarePaymentMethod'
      ) {
        return `Charge Credit Card ${formattedAmount}`;
      }
      return this.props.viewer
        ? `Record Payment of ${formattedAmount}`
        : 'Done';
    })();

    const PayButtonContent = (
      <span>
        {/* Wrapped in this condition because loader doesn't hide
          on mobile despite having active set to false */}
        {this.state.submitting &&
          <Loader
            active={this.state.submitting}
            inline
            inverted
            size='mini'
            style={{ marginRight: '1em' }}
          />}
        {payButtonText}
      </span>
    );

    const PayButton = (
      <Button
        primary
        disabled={
          this.state.submitting ||
          (this.state.payment.payment_method.__typename ===
            'SquarePaymentMethod' && !this.state.card)
        }
        onClick={() => {
          if (
            this.state.payment.payment_method.__typename ===
            'SquarePaymentMethod'
          ) {
            this.refs.squareForm.submit();
          } else if (this.props.viewer) {
            this.refs.manualPaymentForm.submit();
          } else {
            this.onClose();
          }
        }}
      >
        {PayButtonContent}
      </Button>
    );

    const SuccessMessage = (
      <div style={{ textAlign: 'center' }}>
        <Success />
        <h3 style={{ marginBottom: '2em' }}>
          You have {this.props.viewer ? 'added' : 'made'} a payment
        </h3>
        <Button basic fluid content='Done' onClick={this.onClose} />
      </div>
    );

    return (
      <span>
        {window.width >= styleVariables.tabletBreakpoint &&
          <Modal
            trigger={this.props.trigger}
            onClose={this.onClose}
            onOpen={this.onOpen}
            open={this.props.open}
            size='small'
          >
            {this.state.step === 'selectAmount' && [
              <Modal.Header content='Pay Invoice' key='header' />,
              <Modal.Content key='content'>
                <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                  {SelectAmount}
                </div>
              </Modal.Content>,
              <Modal.Actions key='actions'>
                <a
                  onClick={this.onClose}
                  className={documentStyles.modalSecondaryAction}
                >
                  Cancel
                </a>
                <Button
                  primary
                  type='submit'
                  disabled={
                    !this.state.paymentAmount ||
                    (this.state.paymentAmount === 'other' &&
                      !(this.state.payment.amount_cents > 0))
                  }
                  onClick={() => this.setState({ step: 'selectPaymentMethod' })}
                >
                  Next
                </Button>
              </Modal.Actions>
            ]}
            {this.state.step === 'selectPaymentMethod' && [
              <Modal.Header content='Pay Invoice' key='header' />,
              <Modal.Content key='content'>
                <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                  {paymentOptions}
                </div>
              </Modal.Content>,
              <Modal.Actions key='actions'>
                <a
                  onClick={() => this.setState({ step: 'selectAmount' })}
                  className={documentStyles.modalSecondaryAction}
                >
                  Back
                </a>
                <Button
                  primary
                  onClick={() => this.setState({ step: 'pay' })}
                  disabled={!this.state.payment.payment_method.id}
                >
                  Next
                </Button>
              </Modal.Actions>
            ]}
            {this.state.step === 'pay' && [
              <Modal.Header content='Pay Invoice' key='header' />,
              <Modal.Content key='content'>
                <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                  {PayFields}
                  {squarePaymentMethod && SquareScript}
                </div>
              </Modal.Content>,
              <Modal.Actions key='actions'>
                <a
                  onClick={() => this.setState({ step: 'selectPaymentMethod' })}
                  className={documentStyles.modalSecondaryAction}
                >
                  Back
                </a>
                {PayButton}
              </Modal.Actions>
            ]}
            {this.state.step === 'success' && [
              <Modal.Header content='Pay Invoice' key='header' />,
              <Modal.Content key='content'>
                <div style={{ maxWidth: '280px', margin: '0 auto' }}>
                  {SuccessMessage}
                </div>
              </Modal.Content>
            ]}
          </Modal>}
        {window.width < styleVariables.tabletBreakpoint &&
          <MobileModal
            trigger={this.props.trigger}
            open={this.props.open}
            onClose={this.onClose}
            onOpen={this.onOpen}
          >
            {this.state.step === 'selectAmount' && [
              <MobileModal.Header key='header'>
                <div className='menu'>
                  <a className='item' onClick={this.onClose}>
                    Cancel
                  </a>
                </div>
              </MobileModal.Header>,
              <MobileModal.Content key='content'>
                {SelectAmount}
                <Button
                  primary
                  style={{ marginTop: '2em' }}
                  disabled={
                    !this.state.paymentAmount ||
                    (this.state.paymentAmount === 'other' &&
                      !(this.state.payment.amount_cents > 0))
                  }
                  onClick={() => this.setState({ step: 'selectPaymentMethod' })}
                >
                  Next
                </Button>
              </MobileModal.Content>
            ]}
            {this.state.step === 'selectPaymentMethod' && [
              <MobileModal.Header key='header'>
                <div className='menu'>
                  <a
                    className='item'
                    onClick={() => this.setState({ step: 'selectAmount' })}
                  >
                    Back
                  </a>
                </div>
              </MobileModal.Header>,
              <MobileModal.Content key='content'>
                {paymentOptions}
                {this.paymentMethodsExist() &&
                  <Button
                    primary
                    onClick={() => this.setState({ step: 'pay' })}
                    disabled={!this.state.payment.payment_method.id}
                  >
                    Next
                  </Button>}
              </MobileModal.Content>
            ]}
            {this.state.step === 'pay' && [
              <MobileModal.Header key='header'>
                <div className='menu'>
                  <a
                    className='item'
                    onClick={() =>
                      this.setState({ step: 'selectPaymentMethod' })}
                  >
                    Back
                  </a>
                </div>
              </MobileModal.Header>,
              <MobileModal.Content key='content'>
                {PayFields}
                {squarePaymentMethod && SquareScript}
                <br />
                {PayButton}
              </MobileModal.Content>
            ]}
            {this.state.step === 'success' &&
              <div>
                <MobileModal.Content>
                  {SuccessMessage}
                </MobileModal.Content>
              </div>}
          </MobileModal>}
      </span>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/Invoice/Pay.js